import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axios } from 'utils/axios';
import { Container, Typography, Box, CircularProgress, Alert, Button, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

const ViewerContainer = styled(Paper)(({ theme }) => ({
  height: '80vh',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[5],
  overflow: 'hidden',
  backgroundColor: '#f9fafb',
  marginTop: theme.spacing(2)
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  background: 'theme.palette.primary.main',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[1],
  color: '#ffffff',
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`
}));

const TransactionPdfViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        const response = await axios.get(`/rest/api/transactions/${id}/download-pdf`, {
          responseType: 'arraybuffer',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfData(url);
        setLoading(false);
      } catch (err: any) {
        setError(`Failed to load PDF: ${err.response?.data?.message || err.message}`);
        setLoading(false);
      }
    };

    fetchPdf();

    return () => {
      if (pdfData) {
        URL.revokeObjectURL(pdfData);
      }
    };
  }, [id]);

  // Fetches the PDF for the transaction
  const fetchPdf = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/rest/api/transactions/${id}/download-pdf`, {
        responseType: 'arraybuffer',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfData(url);
    } catch (err: any) {
      setError(`Failed to load PDF: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <HeaderContainer>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Transaction PDF Viewer
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard/transactions/list')}
          sx={{
            backgroundColor: '#c79327',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#c79327' }
          }}
        >
          Back to Transactions
        </Button>
      </HeaderContainer>
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {pdfData && (
        <ViewerContainer elevation={3}>
          <iframe
            src={pdfData}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Transaction PDF"
          />
        </ViewerContainer>
      )}
    </Container>
  );
};

export default TransactionPdfViewer;
